import type { Bookmark, Editor } from "tinymce";

import { SCRIBE_GUIDE_COPY } from "features/medical-scribe/getting-started/scribe-guide.copy";
import { getMedicalScribeText } from "features/medical-scribe/medical-scribes-section/scribes-list/utils";
import {
  AMBIENT_SCRIBE_TEMPLATE_TYPE,
  type AmbientScribeTemplateType
} from "services/types/ambient-scribe-template.type";
import type { AmbientScribe } from "services/types/ambient-scribe.type";

export type ScribeInsertAction = "copy" | "replace" | "insert" | "noop";

/**
 * The editor handle only if it is still usable, otherwise `null`.
 *
 * The ref this comes from is app-wide and outlives the editors it points at: tinymce-react calls
 * `editor.remove()` in `componentWillUnmount`, and `insertContent()` on a removed instance is a
 * silent no-op — the insert reports success and nothing appears. Treat a dead handle as "no
 * editor" so the caller falls back to the clipboard and says so.
 */
export function getUsableEditor(
  editor: Editor | null | undefined
): Editor | null {
  return editor && !editor.removed && editor.initialized ? editor : null;
}

/**
 * Puts the caret where an insert should land, and reports which rule decided it.
 *
 * **Never let `insertContent` run on TinyMCE's default range.** An editor the user has not focused
 * still reports a valid range — at the START of the body — and inserting there is a *silent no-op*
 * when the body opens with an inline `contenteditable="false"` node, which is every chart note whose
 * template begins with a merge tag. Even when it does land, it prepends the scribe above the
 * clinician's own documentation. Verified against TinyMCE 7.4.1.
 *
 * So: restore the caret the user actually left (via the bookmark the opener captured), and otherwise
 * append at the end — the natural place to add a scribe note to existing documentation.
 */
export function placeInsertCaret(
  editor: Editor,
  bookmark: Bookmark | null
): "bookmark" | "end" {
  if (bookmark) {
    try {
      editor.selection.moveToBookmark(bookmark);
      return "bookmark";
    } catch {
      // Bookmark belongs to another editor, or its nodes are gone. Fall through to appending
      // rather than inserting at whatever the stale selection happens to be.
    }
  }
  editor.selection.select(editor.getBody(), true);
  editor.selection.collapse(false);
  return "end";
}

/**
 * The raw text the active tab would insert. Markdown-to-HTML conversion stays with the caller
 * (react-markdown is JSX), so this is pure and directly testable.
 *
 * The audiology branch is the reason this is not a one-liner: when the dialog was opened from an
 * audiology report, the Scribe tab shows a note from `GET /ai/ambient-scribes/notes/`, and
 * `soap_notes` is NEVER written by that flow. Reading the scribe there would insert an unrelated
 * SOAP note, or silently nothing.
 */
export function getScribeInsertSourceText(params: {
  tabView: string;
  scribe: AmbientScribe | null | undefined;
  isAudiologyMode: boolean;
  audiologyNoteHtml: string | undefined;
}): string {
  const { tabView, scribe, isAudiologyMode, audiologyNoteHtml } = params;

  if (isAudiologyMode && tabView === "soap") {
    return String(audiologyNoteHtml ?? "");
  }
  if (tabView === "summary") {
    return String(scribe?.summary ?? "");
  }
  if (tabView === "soap") {
    return String(scribe?.soap_notes ?? "");
  }
  return getMedicalScribeText(scribe);
}

/** Only the rich-text tabs may carry markdown; a transcript is assembled as HTML already. */
export function isMarkdownConvertibleTab(tabView: string): boolean {
  return tabView === "summary" || tabView === "soap";
}

/**
 * Decides what to do with the resolved scribe text when the insert dialog is
 * submitted.
 *
 * Guards against an empty "Replace existing content" wiping the chart note
 * (SN-18246): when there is no resolved text (empty Summary/SOAP tab, or a
 * scribe that has not finished loading), a replace becomes a no-op instead of
 * clearing the editor. The normal insert path is unaffected.
 */
export function getScribeInsertAction(params: {
  hasEditor: boolean;
  needReplace: boolean;
  text: string;
}): ScribeInsertAction {
  const { hasEditor, needReplace, text } = params;
  if (!hasEditor) {
    return "copy";
  }
  if (!needReplace) {
    return "insert";
  }
  return text.trim() ? "replace" : "noop";
}

/**
 * Opened from an audiology report's summary toolbar (SN-19067): the Scribe tab then shows the
 * generated report summary for this report instead of the scribe's SOAP notes, and Insert writes
 * that note rather than `soap_notes`.
 */
export function isAudiologyInsertMode(params: {
  isTemplateTypesEnabled: boolean;
  templateType: AmbientScribeTemplateType | undefined;
  audiologyReportId: number | undefined;
}): boolean {
  const { isTemplateTypesEnabled, templateType, audiologyReportId } = params;
  return (
    isTemplateTypesEnabled &&
    templateType === AMBIENT_SCRIBE_TEMPLATE_TYPE.AUDIOLOGY_REPORT &&
    audiologyReportId != null
  );
}

/**
 * Whether the open tab has anything Insert could write. The letter (AVS) tab never does: it is
 * emailed from its own tab, never inserted.
 */
export function hasInsertableContent(params: {
  tabView: string;
  scribe: AmbientScribe | null | undefined;
  isAudiologyMode: boolean;
  audiologyNoteHtml: string | undefined;
}): boolean {
  const { tabView, scribe, isAudiologyMode, audiologyNoteHtml } = params;
  if (tabView === "summary") {
    return Boolean(scribe?.summary);
  }
  if (tabView === "soap") {
    // In audiology mode Insert stays disabled while the only run is a failure, or none has happened.
    return Boolean(isAudiologyMode ? audiologyNoteHtml : scribe?.soap_notes);
  }
  if (tabView === "transcript") {
    return Boolean(scribe?.transcription);
  }
  return false;
}

/** The Insert button's label: "Insert" with an editor to write into, "Copy" without one. */
export function getInsertButtonLabel(params: {
  tabView: string;
  hasEditor: boolean;
  isAudiologyMode: boolean;
}): string {
  const { tabView, hasEditor, isAudiologyMode } = params;
  const prefix = hasEditor ? "Insert" : "Copy";
  if (tabView === "summary") return `${prefix} Summary`;
  // "Summary" alone would collide with the scribe's own Summary tab above.
  if (tabView === "soap" && isAudiologyMode) return `${prefix} Report Summary`;
  if (tabView === "soap") return `${prefix} Scribe`;
  // Only stops the label reading "Insert Transcript" on the letter tab; the action stays disabled.
  if (tabView === "avs") return `${prefix} Letter`;
  return `${prefix} Transcript`;
}

/**
 * Gets words around a specific position in a string
 * @param {string} text - The input text
 * @param {number} position - Character position in the text
 * @param {number} wordsBefore - Number of words to get before position (default 3)
 * @param {number} wordsAfter - Number of words to get after position (default 3)
 * @returns {Object} Object with before and after text
 */
export function getWordsAround(
  text: string,
  position: number,
  wordsBefore: number = 3,
  wordsAfter: number = 3
) {
  if (position < 0 || position > text.length) {
    return { before: "", after: "", atPosition: "" };
  }

  // Get words before position
  let beforeText = "";
  let beforeCount = 0;
  let beforePos = position;

  while (beforePos > 0 && beforeCount < wordsBefore) {
    beforePos--;
    // Check for word boundary
    if (
      text[beforePos] === " " &&
      beforePos + 1 < text.length &&
      text[beforePos + 1] !== " "
    ) {
      beforeCount++;
    }
  }
  beforeText = text.slice(beforePos, position).trim();

  // Get words after position
  let afterText = "";
  let afterCount = 0;
  let afterPos = position;

  while (afterPos < text.length && afterCount < wordsAfter) {
    afterPos++;
    // Check for word boundary
    if (
      (text[afterPos] === " " || afterPos === text.length) &&
      afterPos > 0 &&
      text[afterPos - 1] !== " "
    ) {
      afterCount++;
    }
  }
  afterText = text.slice(position, afterPos).trim();

  // Get the exact word at position
  let atPositionWord = "";
  const wordAtPosRegex = /\S+/;
  const match = text.slice(position).match(wordAtPosRegex);
  if (match) {
    atPositionWord = match[0];
  }

  return {
    before: beforeText,
    after: afterText,
    atPosition: atPositionWord
  };
}

/** Opened from an audiology report: the button that opened the dialog promises generation. */
export const AUDIOLOGY_SELECT_PROMPT =
  "Select a recording to generate the report summary from";

/**
 * What the right panel says while no recording is selected, with the Getting Started guide on.
 * Asking a patient with no recordings to "select" one sends the user looking for a list that is
 * empty, so that case says what the panel will hold instead.
 */
export function getEmptyPanelPrompt({
  hasRecordings,
  isAudiologyMode
}: {
  hasRecordings: boolean;
  isAudiologyMode: boolean;
}): string {
  if (!hasRecordings) {
    return isAudiologyMode
      ? SCRIBE_GUIDE_COPY.insertScribeNoRecordingsAudiology
      : SCRIBE_GUIDE_COPY.insertScribeNoRecordings;
  }
  return isAudiologyMode
    ? AUDIOLOGY_SELECT_PROMPT
    : SCRIBE_GUIDE_COPY.insertScribeSelectPrompt;
}
